import React from 'react';
import { TextField, MenuItem } from '@mui/material';
import { internalChannelsApi } from '../services/api';
import { authService } from '../services/auth';

export interface PhoneChannel {
  channel_id: string;
  channel_value: string;
  channel_name: string;
  channel_type: string;
  voice_enabled: boolean;
}

// Global cache for phone channels
let channelsCache: PhoneChannel[] | null = null;
let fetchingPromise: Promise<PhoneChannel[]> | null = null;

export const fetchPhoneChannels = async (): Promise<PhoneChannel[]> => {
  // Return empty array if not authenticated
  if (!authService.isAuthenticated()) {
    return [];
  }

  if (channelsCache) return channelsCache;
  if (fetchingPromise) return fetchingPromise;

  fetchingPromise = internalChannelsApi.getInternalChannels().then((results: any) => {
    // Filter only phone channels that have voice enabled
    const phoneChannels = results.filter((channel: PhoneChannel) => 
      channel.channel_type === 'phone' && channel.voice_enabled
    );
    channelsCache = phoneChannels;
    fetchingPromise = null;
    return phoneChannels;
  }).catch((err) => {
    console.error('Error fetching phone channels:', err);
    fetchingPromise = null;
    throw err;
  });

  return fetchingPromise;
};

// Only prefetch if authenticated
if (authService.isAuthenticated()) {
  fetchPhoneChannels().catch(console.error);
}

interface PhoneNumberSelectProps {
  value: string;
  onChange: (value: string, channelId: string) => void;
  label?: string;
  fullWidth?: boolean;
  channels?: PhoneChannel[];
  showAllOption?: boolean;
  sx?: any;
}

const PhoneNumberSelect: React.FC<PhoneNumberSelectProps> = ({
  value,
  onChange,
  label = 'From Number',
  fullWidth = true,
  channels = channelsCache || [],
  showAllOption = false,
  sx
}) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedChannel = channels.find(channel => channel.channel_value === e.target.value);
    onChange(e.target.value, selectedChannel?.channel_id || '');
  };

  return (
    <TextField
      select
      fullWidth={fullWidth}
      label={label}
      value={value}
      onChange={handleChange}
      sx={sx}
      SelectProps={{
        renderValue: (selected) => selected as string
      }}
    >
      {showAllOption && <MenuItem value="">All</MenuItem>}
      {channels.map((channel) => (
        <MenuItem key={channel.channel_id} value={channel.channel_value}>
          {channel.channel_name} ({channel.channel_value})
        </MenuItem>
      ))}
    </TextField>
  );
};

export default PhoneNumberSelect; 